import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { fallbackInterval, useRealtimeConnected } from '@/lib/realtime';
import { apiMessage } from './LabBenchErrors';
import { LabBenchReasonDialog } from './LabBenchReasonDialog';

interface EditRequestRow {
  id: string;
  status: string;
  reason: string;
  requestedAt: string;
  requestedBy: string | null;
  requestedByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  orderNumber: string | null;
  accessionNo: string | null;
  testName: string | null;
  patient: { fullName: string; mrn: string } | null;
  newValue: { value: string; unit: string | null; flag: string } | null;
  analyserValue: { value: string; unit: string | null; flag: string | null } | null;
}

interface EditRequestPage {
  data: EditRequestRow[];
  nextCursor: string | null;
}

const STATUSES = [
  { value: 'pending', label: 'Waiting' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Refused' },
  { value: 'cancelled', label: 'Replaced by a newer value' },
];

export function LabApprovalsPage() {
  const { can } = useAuth();
  const kinds = [
    ...(can('lab.result.edit.approve', 'lab.result.verify') ? [{ value: 'results', label: tr('lab', 'changes_to_analyser_values') }] : []),
    ...(can('lab.label.reprint.approve') ? [{ value: 'labels', label: tr('lab', 'label_reprints') }] : []),
  ];
  const [kind, setKind] = useState(kinds[0]?.value ?? 'results');
  const kindField =
    kinds.length > 1 ? (
      <FilterField label={tr('lab', 'what_to_approve')}>
        <Select className="w-64" value={kind} onChange={(e) => setKind(e.target.value)} aria-label={tr('lab', 'what_to_approve')}>
          {kinds.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </Select>
      </FilterField>
    ) : null;
  return kind === 'labels' ? <ReprintApprovals kindField={kindField} /> : <ResultChangeApprovals kindField={kindField} />;
}

function loadMoreFooter(query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown }) {
  if (!query.hasNextPage) return undefined;
  return (
    <div className="flex w-full justify-center">
      <Button variant="outline" size="sm" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
        {tr('lab', 'load_more')}
      </Button>
    </div>
  );
}

interface ReprintRow {
  id: string;
  accessionNo: string;
  reason: string;
  status: string;
  requestedBy: string | null;
  requestedByName: string | null;
  requestedAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  expiresAt: string | null;
  consumedAt: string | null;
}

function ReprintApprovals({ kindField }: { kindField: ReactNode }) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const live = useRealtimeConnected();
  const requests = useInfiniteQuery({
    queryKey: ['lab-reprint-requests', status],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<{ data: ReprintRow[]; nextCursor: string | null }>(`/lab/labels/reprint-requests${qs({ status, limit: 50, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: status === 'pending' ? fallbackInterval(live, 120_000) : false,
  });
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) => post(`/lab/labels/reprint-requests/${id}/decide`, { action }),
    onSuccess: (_result, variables) => {
      toast.success(variables.action === 'approve' ? tr('lab', 'reprint_approved_for_24_hours') : tr('lab', 'reprint_refused'));
      void queryClient.invalidateQueries({ queryKey: ['lab-reprint-requests'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_record_the_decision'), apiMessage(error)),
  });
  const rows = requests.data?.pages.flatMap((page) => page.data) ?? [];
  return (
    <>
      <SearchFilterBar
        filters={
          <>
            {kindField}
            <FilterField label={tr('lab', 'status')}>
              <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={tr('lab', 'status')}>
                <option value="pending">{tr('lab', 'waiting')}</option>
                <option value="approved">{tr('lab', 'approved')}</option>
                <option value="rejected">{tr('lab', 'refused')}</option>
              </Select>
            </FilterField>
          </>
        }
        onReset={status !== 'pending' ? () => setStatus('pending') : undefined}
      />
      <DataTable
        rows={rows}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty={status === 'pending' ? tr('lab', 'no_label_reprint_is_waiting') : tr('lab', 'nothing_here')}
        footer={loadMoreFooter(requests)}
        columns={[
          { header: tr('lab', 'accession'), sort: (row) => row.accessionNo, cell: (row) => <span className="font-mono text-xs">{row.accessionNo}</span> },
          {
            header: tr('lab', 'why'),
            sort: (row) => row.requestedAt,
            cell: (row) => (
              <div className="text-xs">
                <p>{row.reason}</p>
                <p className="text-muted-foreground">
                  {row.requestedByName ?? tr('lab', 'someone')} · {dateTime(row.requestedAt)}
                </p>
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status === 'pending' ? (
                row.requestedBy === user?.id ? (
                  <Badge variant="muted">{tr('lab', 'your_request')}</Badge>
                ) : (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" onClick={() => decide.mutate({ id: row.id, action: 'approve' })}>
                      {tr('lab', 'approve')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: row.id, action: 'reject' })}>
                      {tr('lab', 'refuse')}
                    </Button>
                  </div>
                )
              ) : (
                <span className="text-xs text-muted-foreground">
                  {row.decidedByName ?? ''} {row.consumedAt ? tr('lab', 'printed') : row.expiresAt ? tr('lab', 'until_x', { expiresAt: dateTime(row.expiresAt) }) : ''}
                </span>
              ),
          },
        ]}
      />
    </>
  );
}

function ResultChangeApprovals({ kindField }: { kindField: ReactNode }) {
  const { can, user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [refusing, setRefusing] = useState<EditRequestRow | null>(null);
  const mayDecide = can('lab.result.edit.approve');
  const live = useRealtimeConnected();

  const requests = useInfiniteQuery({
    queryKey: ['lab-edit-requests', status],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<EditRequestPage>(`/lab/result-edit-requests${qs({ status, limit: 50, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: status === 'pending' ? fallbackInterval(live, 120_000) : false,
  });

  const decide = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: 'approve' | 'reject'; note?: string }) =>
      post(`/lab/result-edit-requests/${id}/decide`, { action, note }),
    onSuccess: (_result, variables) => {
      toast.success(variables.action === 'approve' ? tr('lab', 'change_approved') : tr('lab', 'change_refused_the_analyser_value_is_back'));
      setRefusing(null);
      void queryClient.invalidateQueries({ queryKey: ['lab-edit-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_record_the_decision'), apiMessage(error)),
  });

  const rows = requests.data?.pages.flatMap((page) => page.data) ?? [];
  const show = (value: { value: string; unit: string | null } | null) => (value ? `${value.value} ${value.unit ?? ''}`.trim() : '—');

  return (
    <>
      <SearchFilterBar
        filters={
          <>
            {kindField}
            <FilterField label={tr('lab', 'status')}>
              <Select className="w-64" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={tr('lab', 'status')}>
                {STATUSES.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        onReset={status !== 'pending' ? () => setStatus('pending') : undefined}
      />
      <DataTable
        rows={rows}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty={status === 'pending' ? tr('lab', 'no_change_to_an_analyser_value_is_waiting') : tr('lab', 'nothing_here')}
        footer={loadMoreFooter(requests)}
        columns={[
          {
            header: tr('lab', 'patient'),
            sort: (row) => row.patient?.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient?.fullName ?? '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient?.mrn ?? ''} {row.orderNumber ? `· ${row.orderNumber}` : ''}
                </p>
              </div>
            ),
          },
          { header: tr('lab', 'test'), sort: (row) => row.testName, cell: (row) => <span>{row.testName ?? '—'}</span> },
          {
            header: tr('lab', 'analyser_changed_to'),
            cell: (row) => (
              <span>
                <span className="text-muted-foreground">{show(row.analyserValue)}</span> → <span className="font-medium">{show(row.newValue)}</span>
              </span>
            ),
          },
          {
            header: tr('lab', 'why'),
            sort: (row) => row.requestedAt,
            cell: (row) => (
              <div className="max-w-[18rem] text-xs">
                <p>{row.reason}</p>
                <p className="text-muted-foreground">
                  {row.requestedByName ?? tr('lab', 'someone')} · {dateTime(row.requestedAt)}
                </p>
                {row.decidedAt && (
                  <p className="text-muted-foreground">
                    {row.decidedByName ?? tr('lab', 'someone')} · {dateTime(row.decidedAt)}
                    {row.decisionNote ? ` · ${row.decisionNote}` : ''}
                  </p>
                )}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status === 'pending' && mayDecide ? (
                row.requestedBy === user?.id ? (
                  <Badge variant="muted">{tr('lab', 'your_request')}</Badge>
                ) : (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" onClick={() => decide.mutate({ id: row.id, action: 'approve' })} loading={decide.isPending && decide.variables?.id === row.id && decide.variables.action === 'approve'}>
                      {tr('lab', 'approve')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRefusing(row)}>
                      {tr('lab', 'refuse')}
                    </Button>
                  </div>
                )
              ) : (
                <Badge variant="muted">{STATUSES.find((entry) => entry.value === row.status)?.label ?? row.status}</Badge>
              ),
          },
        ]}
      />
      {refusing && (
        <LabBenchReasonDialog
          title={`Refuse the change to ${refusing.testName ?? 'this result'}`}
          description={tr('lab', 'the_analyser_value_x_goes_back_on_the_result_as', { analyserValue: show(refusing.analyserValue) })}
          label={tr('lab', 'why_is_the_change_refused')}
          confirmLabel={tr('lab', 'refuse_and_restore')}
          minLength={3}
          destructive
          pending={decide.isPending}
          onSubmit={(note) => decide.mutate({ id: refusing.id, action: 'reject', note })}
          onClose={() => setRefusing(null)}
        />
      )}
    </>
  );
}
